import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Select } from '../../components/Common';
import OnboardGatewayModal from '../../components/Modal/OnboardGatewayModal';
import WaitingGatewayRow from './WaitingGatewayRow';
import WaitingGatewaysModal from '../../components/Modal/WaitingGatewaysModal';
import useDeviceSerials from '../../hooks/useDeviceSerials';

// Held gateways shown inline; the rest are behind "See all".
export const WAITING_PREVIEW = 3;

const GatewaySection = ({ gatewayDevices, waitingGateways = [], customerId, facilities, onSuccess }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [allWaitingOpen, setAllWaitingOpen] = useState(false);
    const waitingPreview = waitingGateways.slice(0, WAITING_PREVIEW);
    const previewSerials = useDeviceSerials(waitingPreview.map(d => d.id));

    useEffect(() => {
        if (gatewayDevices.length && !selectedDeviceId) {
            setSelectedDeviceId(gatewayDevices[0].id);
        }
    }, [gatewayDevices, selectedDeviceId]);

    const hasGateways = gatewayDevices.length > 0;
    const scheduleDeviceId = selectedDeviceId || gatewayDevices[0]?.id || '';

    return (
        <div className="card-wrapper gateway-section">
            <div className="customer-details__card-header">Emulate Gateways</div>

            {hasGateways ? (
                <>
                    <div className="gateway-section__overview">
                        <span className="gateway-section__count">
                            {gatewayDevices.length} {gatewayDevices.length === 1 ? 'gateway' : 'gateways'} on this customer
                        </span>
                        <Link className="gateway-section__link" to={`/devicesdetail/${customerId}/gateways`}>
                            <span>View gateways</span>
                            <i className="fa-solid fa-arrow-right"></i>
                        </Link>
                    </div>

                    <div className="gateway-section__block">
                        <span className="gateway-section__block-title">Schedule mode on device</span>
                        <div className="gateway-section__scheduler">
                            <Select
                                className="gateway-section__select"
                                name="schedule-device"
                                fullWidth
                                options={gatewayDevices.map(device => ({
                                    value: device.id,
                                    label: device.custom_device_name || device.name || device.id,
                                }))}
                                value={scheduleDeviceId}
                                onChange={e => setSelectedDeviceId(e.target.value)}
                            />
                            <a className="gateway-section__link" href={scheduleDeviceId ? `/devices/${scheduleDeviceId}/mode-scheduler` : '#'}>
                                <span>Go to Scheduler</span>
                                <i className="fa-solid fa-arrow-right"></i>
                            </a>
                        </div>
                    </div>
                </>
            ) : (
                !waitingGateways.length && <p className="gateway-section__empty">No gateways onboarded for this customer yet.</p>
            )}

            {waitingGateways.length > 0 && (
                <div className="gateway-section__block gateway-section__waiting" data-testid="waiting-gateways">
                    <div className="gateway-section__waiting-header">
                        <span className="gateway-section__block-title">Awaiting connection ({waitingGateways.length})</span>
                        {waitingGateways.length > WAITING_PREVIEW && (
                            <button
                                type="button"
                                className="gateway-section__link gateway-section__link--button"
                                onClick={() => setAllWaitingOpen(true)}
                            >
                                <span>See all {waitingGateways.length}</span>
                                <i className="fa-solid fa-arrow-right"></i>
                            </button>
                        )}
                    </div>
                    <ul className="gateway-section__waiting-list">
                        {waitingPreview.map(device => (
                            <WaitingGatewayRow key={device.id} device={device} serial={previewSerials[device.id]} facilities={facilities} />
                        ))}
                    </ul>
                    <span className="gateway-section__waiting-hint">
                        Onboarding has been recorded. It completes automatically once the gateway connects to Emulate, and the gateway then appears
                        above.
                    </span>
                    <WaitingGatewaysModal open={allWaitingOpen} setOpen={setAllWaitingOpen} devices={waitingGateways} facilities={facilities} />
                </div>
            )}

            <div className="gateway-section__block">
                <span className="gateway-section__block-title">Onboard a gateway</span>
                <Button text="Onboard Gateway Remotely" onClick={() => setModalOpen(true)} />
            </div>

            <OnboardGatewayModal
                facilities={facilities}
                open={modalOpen}
                setOpen={setModalOpen}
                onSuccess={() => {
                    setModalOpen(false);
                    if (onSuccess) {
                        onSuccess();
                    }
                }}
            />
        </div>
    );
};

export default GatewaySection;
